import { BillingCalculation } from '../types/billing';

const API_BASE_URL = (import.meta.env.VITE_API_URL || 'https://awbbackend.bliitz.in')
    .replace(/\/api\/?$/, '')
    .replace(/\/$/, '');

class BillingService {
    private getAuthHeaders(includeJson = true) {
        const headers: Record<string, string> = {};
        if (includeJson) {
            headers['Content-Type'] = 'application/json';
        }
        const token = localStorage.getItem('accessToken');
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        return headers;
    }

    private async handleResponse(response: Response) {
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Network error' }));
            throw new Error(errorData.error?.message || errorData.message || 'API request failed');
        }
        return response.json();
    }

    private buildQueryParams(params: Record<string, unknown>) {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value === undefined || value === null) return;
            const stringValue = String(value);
            if (!stringValue || stringValue.toLowerCase() === 'all') return;
            searchParams.append(key, stringValue);
        });
        return searchParams;
    }

    // AWB Operations for Billing
    async getAWBsForBilling(params: {
        billingType?: 'SALES' | 'PURCHASE';
        customerId?: string;
        vendorId?: string;
        startDate?: string;
        endDate?: string;
        status?: string;
        serviceId?: string;
        destinationCode?: string;
        minAmount?: string | number;
        maxAmount?: string | number;
        page?: number;
        limit?: number;
    } = {}) {
        try {
            // First try the billing-specific endpoint
            const billingParams = this.buildQueryParams(params);

            try {
                const response = await fetch(
                    `${API_BASE_URL}/api/billing/awbs?${billingParams.toString()}`,
                    {
                        method: 'GET',
                        headers: this.getAuthHeaders()
                    }
                );

                if (response.ok) {
                    return this.handleResponse(response);
                }
            } catch (error) {
                console.warn('Billing AWB endpoint not available, falling back to general AWB search');
            }

            // Fallback to general AWB search
            const awbParams = new URLSearchParams({
                limit: (params.limit || 50).toString(),
                page: (params.page || 1).toString()
            });

            const response = await fetch(
                `${API_BASE_URL}/api/awb/search?${awbParams.toString()}`,
                {
                    method: 'GET',
                    headers: this.getAuthHeaders()
                }
            );

            const data = await this.handleResponse(response);

            // Transform AWB data to billing format
            const transformedAWBs = data.data.awbs.map((awb: any) => ({
                _id: awb._id,
                awbNumber: awb.awbNumber,
                bookingDate: awb.bookingDate,
                customer: {
                    _id: awb.customer._id,
                    name: awb.customer.name,
                    code: awb.customer.code,
                    gstNumber: awb.customer.gstNumber
                },
                vendor: awb.vendor ? {
                    _id: awb.vendor._id,
                    name: awb.vendor.name,
                    code: awb.vendor.code,
                    gstNumber: awb.vendor.gstNumber
                } : undefined,
                service: {
                    _id: awb.service._id,
                    name: awb.service.name,
                    serviceType: awb.service.code?.includes('EXPRESS') ? 'EXPRESS' : 'STANDARD'
                },
                destination: {
                    name: awb.destination.name,
                    code: awb.destination.code
                },
                totalChargeableWeight: awb.totalChargeableWeight,
                totalPieces: awb.totalPieces,
                shipmentValue: awb.shipmentValue,
                currentStatus: awb.currentStatus,
                // Add calculated billing amounts (mock for now)
                salesBilling: {
                    finalAmount: this.calculateMockBilling(awb, 'SALES')
                },
                purchaseBilling: {
                    finalAmount: this.calculateMockBilling(awb, 'PURCHASE')
                }
            }));

            return {
                success: true,
                data: {
                    awbs: transformedAWBs,
                    total: data.data.total,
                    page: data.data.page,
                    limit: data.data.limit,
                    totalPages: data.data.totalPages
                }
            };
        } catch (error) {
            console.error('Error fetching AWBs for billing:', error);
            throw error;
        }
    }

    // Billing Calculations
    async calculateSalesBilling(params: {
        awbId: string;
        serviceId: string;
        weight: number;
        shipmentValue: number;
        paymentMethod: string;
        customerId?: string;
        destination?: string;
    }): Promise<{ success: boolean; data?: BillingCalculation; error?: string }> {
        try {
            const response = await fetch(`${API_BASE_URL}/api/billing/calculate-sales`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify(params)
            });

            if (response.ok) {
                return this.handleResponse(response);
            }
        } catch (error) {
            console.warn('Sales billing calculation endpoint not available, using mock calculation');
        }

        // Fallback to mock calculation
        const mockCalculation = this.calculateMockBillingDetailed(params, 'SALES');
        return {
            success: true,
            data: mockCalculation
        };
    }

    async calculatePurchaseBilling(params: {
        awbId: string;
        serviceId: string;
        weight: number;
        shipmentValue: number;
        paymentMethod: string;
        vendorId?: string;
        destination?: string;
    }): Promise<{ success: boolean; data?: BillingCalculation; error?: string }> {
        try {
            const response = await fetch(`${API_BASE_URL}/api/billing/calculate-purchase`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify(params)
            });

            if (response.ok) {
                return this.handleResponse(response);
            }
        } catch (error) {
            console.warn('Purchase billing calculation endpoint not available, using mock calculation');
        }

        // Fallback to mock calculation
        const mockCalculation = this.calculateMockBillingDetailed(params, 'PURCHASE');
        return {
            success: true,
            data: mockCalculation
        };
    }

    // Invoice Operations
    async generateInvoice(params: {
        awbIds: string[];
        customerId?: string;
        vendorId?: string;
        companyId: string;
        invoiceType: 'SALES' | 'PURCHASE';
        billingPeriod: {
            startDate: Date;
            endDate: Date;
        };
    }) {
        try {
            const response = await fetch(`${API_BASE_URL}/api/billing/generate-invoice`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify({
                    ...params,
                    billingPeriod: {
                        startDate: params.billingPeriod.startDate.toISOString(),
                        endDate: params.billingPeriod.endDate.toISOString()
                    }
                })
            });

            if (response.ok) {
                return this.handleResponse(response);
            }
        } catch (error) {
            console.warn('Invoice generation endpoint not available, using mock response');
        }

        // Mock invoice generation
        const mockInvoice = {
            _id: 'inv_' + Date.now(),
            invoiceNumber: `${params.invoiceType === 'SALES' ? 'SI' : 'PI'}-${String(Date.now()).slice(-6)}`,
            invoiceType: params.invoiceType,
            customerName: params.invoiceType === 'SALES' ? 'Mock Customer' : undefined,
            vendorName: params.invoiceType === 'PURCHASE' ? 'Mock Vendor' : undefined,
            invoiceDate: new Date(),
            dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
            totalAmount: params.awbIds.length * 15000, // Mock amount
            paidAmount: 0,
            pendingAmount: params.awbIds.length * 15000,
            status: 'DRAFT',
            currency: 'INR',
            awbs: params.awbIds
        };

        return {
            success: true,
            data: mockInvoice
        };
    }

    // Payment Operations
    async getInvoicesForPayment(params: {
        status?: string;
        invoiceType?: string;
        customerId?: string;
        vendorId?: string;
        startDate?: string;
        endDate?: string;
        page?: number;
        limit?: number;
    } = {}) {
        // Mock invoices data since the endpoint might not be available
        const mockInvoices = [
            {
                _id: '1',
                invoiceNumber: 'SI-001234',
                invoiceType: 'SALES',
                customerName: 'TechCorp India Private Limited',
                invoiceDate: new Date('2025-01-15'),
                dueDate: new Date('2025-02-14'),
                totalAmount: 15000,
                paidAmount: 10000,
                pendingAmount: 5000,
                status: 'SENT',
                currency: 'INR'
            },
            {
                _id: '2',
                invoiceNumber: 'SI-001235',
                invoiceType: 'SALES',
                customerName: 'Mumbai Fashion House',
                invoiceDate: new Date('2025-01-20'),
                dueDate: new Date('2025-02-19'),
                totalAmount: 25000,
                paidAmount: 25000,
                pendingAmount: 0,
                status: 'PAID',
                currency: 'INR'
            },
            {
                _id: '3',
                invoiceNumber: 'PI-001001',
                invoiceType: 'PURCHASE',
                vendorName: 'Global Logistics Partner',
                invoiceDate: new Date('2025-01-10'),
                dueDate: new Date('2025-01-25'),
                totalAmount: 8000,
                paidAmount: 0,
                pendingAmount: 8000,
                status: 'OVERDUE',
                currency: 'INR'
            }
        ];

        return {
            success: true,
            data: {
                invoices: mockInvoices,
                total: mockInvoices.length,
                page: params.page || 1,
                limit: params.limit || 20
            }
        };
    }

    async recordPayment(params: {
        invoiceId: string;
        paymentDate: Date;
        amount: number;
        paymentMethod: string;
        referenceNumber?: string;
        notes?: string;
    }) {
        try {
            const response = await fetch(`${API_BASE_URL}/api/billing/record-payment`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify({
                    ...params,
                    paymentDate: params.paymentDate.toISOString()
                })
            });

            if (response.ok) {
                return this.handleResponse(response);
            }
        } catch (error) {
            console.warn('Payment recording endpoint not available, using mock response');
        }

        // Mock payment recording
        return {
            success: true,
            data: {
                _id: 'pay_' + Date.now(),
                invoiceId: params.invoiceId,
                paymentDate: params.paymentDate,
                amount: params.amount,
                paymentMethod: params.paymentMethod,
                referenceNumber: params.referenceNumber,
                notes: params.notes,
                status: 'COMPLETED'
            }
        };
    }

    // Reports
    async generateBillingReport(startDate: Date, endDate: Date) {
        try {
            const response = await fetch(
                `${API_BASE_URL}/api/billing/report?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`,
                {
                    method: 'GET',
                    headers: this.getAuthHeaders()
                }
            );

            if (response.ok) {
                return this.handleResponse(response);
            }
        } catch (error) {
            console.warn('Billing report endpoint not available, using mock data');
        }

        // Mock report data
        return {
            success: true,
            data: {
                period: { startDate, endDate },
                salesSummary: {
                    totalInvoices: 45,
                    totalAmount: 450000,
                    paidAmount: 380000,
                    pendingAmount: 70000,
                    overdueAmount: 25000
                },
                purchaseSummary: {
                    totalInvoices: 32,
                    totalAmount: 280000,
                    paidAmount: 250000,
                    pendingAmount: 30000,
                    overdueAmount: 15000
                },
                metrics: {
                    totalRevenue: 2850000,
                    totalCost: 1890000,
                    profit: 960000,
                    margin: 33.68,
                    collectionRate: 88.3
                }
            }
        };
    }

    // Helper Methods
    private calculateMockBilling(awb: any, type: 'SALES' | 'PURCHASE'): number {
        const baseRate = awb.totalChargeableWeight * (type === 'SALES' ? 60 : 45);
        const fuelSurcharge = baseRate * 0.12;
        const handlingCharge = awb.totalChargeableWeight * 5;
        const insuranceCharge = awb.shipmentValue * 0.005;
        const totalCharges = baseRate + fuelSurcharge + handlingCharge + insuranceCharge;
        const gstRate = type === 'SALES' ? 0.18 : 0.12;
        const gstAmount = totalCharges * gstRate;
        return totalCharges + gstAmount;
    }

    private calculateMockBillingDetailed(params: any, type: 'SALES' | 'PURCHASE'): BillingCalculation {
        const baseRate = params.weight * (type === 'SALES' ? 60 : 45);
        const fuelSurcharge = baseRate * 0.12;
        const handlingCharge = params.weight * 5;
        const codCharge = params.paymentMethod === 'COD' ? params.shipmentValue * 0.02 : 0;
        const insuranceCharge = params.shipmentValue * 0.005;
        const otherCharges = 0;
        const totalCharges = baseRate + fuelSurcharge + handlingCharge + codCharge + insuranceCharge + otherCharges;
        const gstRate = type === 'SALES' ? 18 : 12;
        const gstAmount = totalCharges * (gstRate / 100);
        const finalAmount = totalCharges + gstAmount;

        return {
            baseRate: Math.round(baseRate * 100) / 100,
            fuelSurcharge: Math.round(fuelSurcharge * 100) / 100,
            handlingCharge: Math.round(handlingCharge * 100) / 100,
            codCharge: Math.round(codCharge * 100) / 100,
            insuranceCharge: Math.round(insuranceCharge * 100) / 100,
            otherCharges: Math.round(otherCharges * 100) / 100,
            totalCharges: Math.round(totalCharges * 100) / 100,
            gstAmount: Math.round(gstAmount * 100) / 100,
            finalAmount: Math.round(finalAmount * 100) / 100,
            currency: 'INR'
        };
    }

    // Master Data for Billing
    async getServices() {
        try {
            const response = await fetch(`${API_BASE_URL}/api/master-data/service`, {
                headers: this.getAuthHeaders()
            });
            if (response.ok) {
                const data = await this.handleResponse(response);
                // Ensure we always return an array
                if (data && Array.isArray(data.data)) {
                    return data.data;
                }
                console.warn('Services API returned invalid data structure, using fallback');
            } else {
                console.warn('Services API request failed with status:', response.status);
            }
        } catch (error) {
            console.warn('Services endpoint not available, using mock data:', error);
        }

        // Always return fallback array
        return [
            { _id: 'express', name: 'Express Delivery', code: 'EXPRESS001', baseRate: 60, fuelSurcharge: 12 },
            { _id: 'standard', name: 'Standard Delivery', code: 'STANDARD001', baseRate: 45, fuelSurcharge: 10 },
            { _id: 'economy', name: 'Economy Delivery', code: 'ECONOMY001', baseRate: 30, fuelSurcharge: 8 }
        ];
    }

    async getCustomers() {
        try {
            const response = await fetch(`${API_BASE_URL}/api/master-data/customer`, {
                headers: this.getAuthHeaders()
            });
            if (response.ok) {
                const data = await this.handleResponse(response);
                // Ensure we always return an array
                if (data && Array.isArray(data.data)) {
                    return data.data;
                }
                console.warn('Customers API returned invalid data structure, using fallback');
            } else {
                console.warn('Customers API request failed with status:', response.status);
            }
        } catch (error) {
            console.warn('Customers endpoint not available, using mock data:', error);
        }

        // Always return fallback array
        return [
            { _id: 'customer1', name: 'TechCorp India Pvt Ltd', code: 'TECH001', gstNumber: '27ABCDE1234F1Z5' },
            { _id: 'customer2', name: 'Mumbai Fashion House', code: 'FASHION001', gstNumber: '27FGHIJ5678K2A6' },
            { _id: 'customer3', name: 'Express Solutions Ltd', code: 'EXPRESS001', gstNumber: '27KLMNO9012L3B7' }
        ];
    }

    async getCountries() {
        try {
            const response = await fetch(`${API_BASE_URL}/api/master-data/country`, {
                headers: this.getAuthHeaders()
            });
            if (response.ok) {
                const data = await this.handleResponse(response);
                // Ensure we always return an array
                if (data && Array.isArray(data.data)) {
                    return data.data;
                }
                console.warn('Countries API returned invalid data structure, using fallback');
            } else {
                console.warn('Countries API request failed with status:', response.status);
            }
        } catch (error) {
            console.warn('Countries endpoint not available, using mock data:', error);
        }

        // Always return fallback array
        return [
            { _id: 'india', name: 'India', code: 'IN', multiplier: 1.0 },
            { _id: 'usa', name: 'United States', code: 'US', multiplier: 1.5 },
            { _id: 'uk', name: 'United Kingdom', code: 'GB', multiplier: 1.4 },
            { _id: 'canada', name: 'Canada', code: 'CA', multiplier: 1.3 }
        ];
    }

    // Health check for billing endpoints
    async checkBillingEndpoints() {
        const endpoints = [
            '/api/billing/awbs',
            '/api/billing/calculate-sales',
            '/api/billing/generate-invoice',
            '/api/billing/record-payment',
            '/api/billing/report'
        ];

        const results = await Promise.allSettled(
            endpoints.map(async (endpoint) => {
                try {
                    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
                        method: 'GET',
                        headers: this.getAuthHeaders()
                    });
                    return { endpoint, available: response.status !== 404 };
                } catch {
                    return { endpoint, available: false };
                }
            })
        );

        return results.map((result) =>
            result.status === 'fulfilled' ? result.value : { endpoint: 'unknown', available: false }
        );
    }
}

export default new BillingService(); 
